import type { Area, Faq, Project, Service } from '@/types'
import type { PageMeta } from '@/constants/page-seo'

import { contact, siteConfig, social } from '@/constants/site'
import { absoluteUrl } from '@/lib/url'
import { brandAssets, siteImages } from '@/constants/assets'
import { serviceSeo } from '@/constants/service-seo'

import {
    areaSeo,
    pageSeo,
    projectSeo,
    serviceSeo as servicePageSeo
} from '@/constants/page-seo'

const ORGANISATION_ID = absoluteUrl('/#organisation')
const WEBSITE_ID = absoluteUrl('/#website')
const LANGUAGE = 'en-GB'
const REGION = 'South-East London and Kent'

/* The office number in full international form, which is what search engines
   read. Derived from the one number in `contact` so the two never drift. */
const INTERNATIONAL_PHONE = contact.phone.replace(/^0/, '+44 ')

const AREAS_SERVED = [
    { '@type': 'AdministrativeArea', name: 'South-East London' },
    { '@type': 'AdministrativeArea', name: 'Kent' },
    { '@type': 'AdministrativeArea', name: 'Royal Borough of Greenwich' },
    { '@type': 'AdministrativeArea', name: 'London Borough of Lewisham' },
    { '@type': 'AdministrativeArea', name: 'London Borough of Southwark' },
    { '@type': 'AdministrativeArea', name: 'London Borough of Bromley' },
    { '@type': 'AdministrativeArea', name: 'London Borough of Bexley' },
    { '@type': 'AdministrativeArea', name: 'Borough of Dartford' },
    { '@type': 'AdministrativeArea', name: 'Sevenoaks District' }
]

/* Matches `contact.hours`, which is what the contact page shows. */
const OPENING_HOURS = [
    {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '08:00',
        closes: '18:00'
    },
    {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday'],
        opens: '08:00',
        closes: '13:00'
    }
]

/* One entry per service keyword and its close variants, plus the regulations
   every job runs under. */
const KNOWS_ABOUT = Array.from(new Set([
    ...Object.values(serviceSeo).flatMap((entry) => [entry.keyword, ...entry.alternateNames]),
    'Side return extensions',
    'Two-storey extensions',
    'Dormer loft conversions',
    'Hip-to-gable loft conversions',
    'RSJ installation',
    'Load-bearing wall removal',
    'Chimney breast removal',
    'Basement tanking',
    'Drone roof surveys',
    'Period property refurbishment',
    'Building Regulations',
    'Permitted development',
    'Party Wall etc. Act 1996'
]))

/* What every quotation is, as the site promises it. No price is published. */
const QUOTATION_OFFER = {
    '@type': 'Offer',
    name: 'Free site visit and fixed written quotation',
    description: 'A free site visit, then a fixed written quotation priced line by line before any work starts.',
    url: absoluteUrl('/contact'),
    seller: { '@id': ORGANISATION_ID },
    areaServed: AREAS_SERVED
}

type ServiceLink = Pick<Service, 'slug' | 'title'>
type AreaLink = Pick<Area, 'slug' | 'name'>

interface OrganisationOptions {
    services?: ServiceLink[]
    areas?: AreaLink[]
    slogan?: string
}

interface PageOptions {
    headline: string
    description: string
}

interface WebPageOptions {
    type?: string
    path: string
    meta: PageMeta
    headline?: string
    mainEntity?: string
    about?: string
    image?: string | null
    imageAlt?: string | null
    keywords?: string[]
}

const pageTitle = (name: string): string => `${name} - ${siteConfig.name}`

const serviceId = (slug: string): string => absoluteUrl(`/services/${slug}#service`)
const areaId = (slug: string): string => absoluteUrl(`/areas/${slug}#service`)
const projectId = (slug: string): string => absoluteUrl(`/projects/${slug}#article`)

const unique = (values: (string | null | undefined)[]): string[] =>
    Array.from(new Set(values.filter((value): value is string => Boolean(value && value.trim()))))

/* Splits a CMS list such as "SE13, SE6" into its parts. */
const splitList = (value?: string | null): string[] =>
    (value ?? '').split(',').map((part) => part.trim()).filter(Boolean)

const websiteNode = (): Record<string, unknown> => ({
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: absoluteUrl('/'),
    name: siteConfig.name,
    description: siteConfig.description,
    inLanguage: LANGUAGE,
    publisher: { '@id': ORGANISATION_ID }
})

/* The page itself, named and described exactly as its title tag and meta
   description read, with the searches it targets as `keywords`. */
const webPageNode = ({
    type = 'WebPage',
    path,
    meta,
    headline,
    mainEntity,
    about = ORGANISATION_ID,
    image,
    imageAlt,
    keywords
}: WebPageOptions): Record<string, unknown> => {
    const url = absoluteUrl(path)
    const allKeywords = unique([...(meta.keywords ?? []), ...(keywords ?? [])])

    return {
        '@type': type,
        '@id': `${url}#webpage`,
        url,
        name: meta.title,
        ...(headline ? { headline } : {}),
        description: meta.description,
        inLanguage: LANGUAGE,
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@id': about },
        ...(mainEntity ? { mainEntity: { '@id': mainEntity } } : {}),
        ...(image ? {
            primaryImageOfPage: {
                '@type': 'ImageObject',
                url: absoluteUrl(image),
                ...(imageAlt ? { caption: imageAlt } : {})
            }
        } : {}),
        ...(allKeywords.length ? { keywords: allKeywords.join(', ') } : {})
    }
}

/* A borough or district name as a place, with London or Kent above it. */
const boroughNode = (borough: string): Record<string, unknown> => ({
    '@type': 'AdministrativeArea',
    name: borough,
    containedInPlace: {
        '@type': 'AdministrativeArea',
        name: /kent/i.test(borough) || /sevenoaks|dartford/i.test(borough) ? 'Kent' : 'London'
    }
})

/* Every claim below is on the page somewhere. Nothing is marked up that a
   visitor cannot also read. Pass the service list to link the business to
   each service it offers, and the area list to name each town it covers. */
export const organisationSchema = ({
    services = [],
    areas = [],
    slogan
}: OrganisationOptions = {}): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@type': ['GeneralContractor', 'HomeAndConstructionBusiness'],
    '@id': ORGANISATION_ID,
    name: siteConfig.name,
    alternateName: 'Capital Craft',
    legalName: siteConfig.legalName,
    description: siteConfig.description,
    disambiguatingDescription: `Director-led residential builders for house extensions, loft conversions, garage conversions, renovations, roofing, damp proofing and structural alterations across ${REGION}.`,
    ...(slogan ? { slogan } : {}),
    url: absoluteUrl('/'),
    telephone: INTERNATIONAL_PHONE,
    email: contact.email,
    image: siteImages.hero,
    logo: brandAssets.favicon,
    address: {
        '@type': 'PostalAddress',
        addressLocality: contact.locality,
        addressRegion: contact.region,
        postalCode: contact.postalArea,
        addressCountry: contact.country
    },
    areaServed: [
        ...AREAS_SERVED,
        ...areas.map((area) => ({
            '@type': 'Place',
            name: area.name,
            url: absoluteUrl(`/areas/${area.slug}`)
        }))
    ],
    openingHoursSpecification: OPENING_HOURS,
    contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: INTERNATIONAL_PHONE,
        email: contact.email,
        areaServed: contact.country,
        availableLanguage: 'English',
        hoursAvailable: OPENING_HOURS
    },
    sameAs: [social.instagram, social.facebook].filter(Boolean),
    knowsAbout: KNOWS_ABOUT,
    keywords: (pageSeo.home.keywords ?? []).join(', '),
    makesOffer: QUOTATION_OFFER,
    ...(services.length ? {
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: `Residential building services in ${REGION}`,
            itemListElement: services.map((service) => ({
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    '@id': serviceId(service.slug),
                    name: service.title,
                    ...(serviceSeo[service.slug] ? { serviceType: serviceSeo[service.slug].keyword } : {}),
                    url: absoluteUrl(`/services/${service.slug}`)
                }
            }))
        }
    } : {})
})

/* The home page as a page, pointing at the business it describes. */
export const homePageSchema = (): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        webPageNode({
            path: '/',
            meta: pageSeo.home,
            mainEntity: ORGANISATION_ID,
            image: siteImages.hero
        }),
        websiteNode()
    ]
})

/* The full service entry used on the services index and on the service's own
   page: the CMS content plus the keyword set from `@/constants/service-seo`.
   Both use the same `@id`, so search engines treat them as one service. */
const detailedServiceNode = (service: Service): Record<string, unknown> => {
    const seo = serviceSeo[service.slug]
    const page = servicePageSeo[service.slug]

    return {
        '@type': 'Service',
        '@id': serviceId(service.slug),
        name: service.title,
        serviceType: seo?.keyword ?? service.title,
        ...(seo?.alternateNames.length ? { alternateName: seo.alternateNames } : {}),
        description: seo?.description ?? service.intro ?? service.summary ?? siteConfig.description,
        ...(service.summary ? { disambiguatingDescription: service.summary } : {}),
        ...(service.tagline ? { slogan: service.tagline } : {}),
        category: 'Residential building',
        url: absoluteUrl(`/services/${service.slug}`),
        mainEntityOfPage: { '@id': `${absoluteUrl(`/services/${service.slug}`)}#webpage` },
        provider: { '@id': ORGANISATION_ID },
        areaServed: AREAS_SERVED,
        audience: { '@type': 'Audience', audienceType: 'Homeowners' },
        ...(service.image ? {
            image: {
                '@type': 'ImageObject',
                url: absoluteUrl(service.image),
                ...(service.image_alt ? { caption: service.image_alt } : {})
            }
        } : {}),
        ...(page?.keywords?.length ? { keywords: page.keywords.join(', ') } : {}),
        offers: { ...QUOTATION_OFFER, itemOffered: { '@id': serviceId(service.slug) } },
        ...(seo?.secondary.length ? {
            hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: service.title,
                itemListElement: seo.secondary.map((name) => ({
                    '@type': 'Offer',
                    itemOffered: {
                        '@type': 'Service',
                        name,
                        serviceType: seo.keyword
                    }
                }))
            }
        } : {})
    }
}

export const servicesPageSchema = (
    services: Service[],
    page: PageOptions
): Record<string, unknown> => {
    const url = absoluteUrl('/services')
    const listId = `${url}#services`

    const keywords = services.flatMap((service) => {
        const seo = serviceSeo[service.slug]

        return seo ? [seo.keyword, ...seo.secondary] : [service.title]
    })

    return {
        '@context': 'https://schema.org',
        '@graph': [
            webPageNode({
                type: 'CollectionPage',
                path: '/services',
                meta: pageSeo.services,
                headline: page.headline,
                mainEntity: listId,
                keywords
            }),
            {
                '@type': 'ItemList',
                '@id': listId,
                name: `Building services in ${REGION}`,
                description: page.description,
                numberOfItems: services.length,
                itemListOrder: 'https://schema.org/ItemListOrderAscending',
                itemListElement: services.map((service, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: detailedServiceNode(service)
                }))
            },
            websiteNode()
        ]
    }
}

/* A single service page: the page, the service in full and the site. */
export const serviceSchema = (service: Service): Record<string, unknown> => {
    const path = `/services/${service.slug}`
    const meta = servicePageSeo[service.slug] ?? {
        title: pageTitle(service.title),
        description: service.intro ?? service.summary ?? siteConfig.description
    }

    return {
        '@context': 'https://schema.org',
        '@graph': [
            webPageNode({
                path,
                meta,
                headline: service.title,
                mainEntity: serviceId(service.slug),
                about: serviceId(service.slug),
                image: service.image,
                imageAlt: service.image_alt,
                keywords: serviceSeo[service.slug]?.secondary
            }),
            detailedServiceNode(service),
            websiteNode()
        ]
    }
}

export const aboutPageSchema = (
    page: PageOptions & { image: string, imageAlt: string }
): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        webPageNode({
            type: 'AboutPage',
            path: '/about',
            meta: pageSeo.about,
            headline: page.headline,
            mainEntity: ORGANISATION_ID,
            image: page.image,
            imageAlt: page.imageAlt
        }),
        websiteNode()
    ]
})

export const contactPageSchema = (page: PageOptions): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        webPageNode({
            type: 'ContactPage',
            path: '/contact',
            meta: pageSeo.contact,
            headline: page.headline,
            mainEntity: ORGANISATION_ID
        }),
        websiteNode()
    ]
})

export const breadcrumbSchema = (
    items: { name: string, path: string }[]
): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path)
    }))
})

/* The headline a case study shows, without the brand suffix of its title tag. */
const projectHeadline = (project: Project): string =>
    `${project.title}${project.location ? `, ${project.location}` : ''}`

const projectPlace = (project: Project): Record<string, unknown> | null => {
    if (!project.location) return null

    const [locality, postalCode] = project.location.split(',').map((part) => part.trim())

    return {
        '@type': 'Place',
        name: project.location,
        address: {
            '@type': 'PostalAddress',
            addressLocality: locality,
            ...(postalCode ? { postalCode } : {}),
            ...(project.borough ? { addressRegion: project.borough } : {}),
            addressCountry: contact.country
        }
    }
}

/* The projects index: every case study, in the order the page lists them. */
export const projectsPageSchema = (projects: Project[]): Record<string, unknown> => {
    const listId = `${absoluteUrl('/projects')}#projects`

    return {
        '@context': 'https://schema.org',
        '@graph': [
            webPageNode({
                type: 'CollectionPage',
                path: '/projects',
                meta: pageSeo.projects,
                mainEntity: listId
            }),
            {
                '@type': 'ItemList',
                '@id': listId,
                name: `Completed building projects in ${REGION}`,
                numberOfItems: projects.length,
                itemListElement: projects.map((project, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: absoluteUrl(`/projects/${project.slug}`),
                    name: projectSeo[project.slug]?.title ?? projectHeadline(project)
                }))
            },
            websiteNode()
        ]
    }
}

export const projectSchema = (project: Project): Record<string, unknown> => {
    const path = `/projects/${project.slug}`
    const seo = projectSeo[project.slug]
    const place = projectPlace(project)
    const description = seo?.description ?? project.summary ?? ''
    const service = project.service_slug ? serviceSeo[project.service_slug] : undefined

    const keywords = unique([
        ...(seo?.keywords ?? []),
        service?.keyword,
        project.service_title,
        project.property_type
    ])

    const images = unique([
        project.cover_image,
        ...(project.gallery ?? [])
    ]).map((image) => absoluteUrl(image))

    return {
        '@context': 'https://schema.org',
        '@graph': [
            webPageNode({
                path,
                meta: {
                    title: seo?.title ?? pageTitle(project.title),
                    description
                },
                headline: projectHeadline(project),
                mainEntity: projectId(project.slug),
                image: project.cover_image,
                imageAlt: project.cover_image_alt,
                keywords
            }),
            {
                '@type': 'Article',
                '@id': projectId(project.slug),
                headline: projectHeadline(project),
                ...(seo ? { alternativeHeadline: seo.title.replace(/\s*\|\s*Capital Craft$/, '') } : {}),
                description,
                ...(project.summary && project.summary !== description ? { abstract: project.summary } : {}),
                articleSection: 'Case study',
                genre: 'Building project case study',
                inLanguage: LANGUAGE,
                url: absoluteUrl(path),
                mainEntityOfPage: { '@id': `${absoluteUrl(path)}#webpage` },
                isPartOf: { '@id': `${absoluteUrl('/projects')}#webpage` },
                author: { '@id': ORGANISATION_ID },
                publisher: { '@id': ORGANISATION_ID },
                ...(project.service_slug ? {
                    about: {
                        '@type': 'Service',
                        '@id': serviceId(project.service_slug),
                        name: project.service_title ?? service?.keyword,
                        ...(service ? { serviceType: service.keyword } : {})
                    }
                } : {}),
                ...(images.length ? { image: images } : {}),
                ...(place ? { contentLocation: place } : {}),
                ...(keywords.length ? { keywords: keywords.join(', ') } : {})
            },
            websiteNode()
        ]
    }
}

/* The areas index: every location page, in the order the page lists them. */
export const areasPageSchema = (areas: Area[]): Record<string, unknown> => {
    const listId = `${absoluteUrl('/areas')}#areas`

    return {
        '@context': 'https://schema.org',
        '@graph': [
            webPageNode({
                type: 'CollectionPage',
                path: '/areas',
                meta: pageSeo.areas,
                mainEntity: listId,
                keywords: areas.map((area) => `Builders in ${area.name}`)
            }),
            {
                '@type': 'ItemList',
                '@id': listId,
                name: `Areas we cover in ${REGION}`,
                numberOfItems: areas.length,
                itemListElement: areas.map((area, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: absoluteUrl(`/areas/${area.slug}`),
                    name: `Builders in ${area.name}`
                }))
            },
            websiteNode()
        ]
    }
}

/* A location page: "Builders in {area}" as a local service, linked to each
   service the page lists, each named for that area. */
export const areaSchema = (area: Area, services: Service[] = []): Record<string, unknown> => {
    const path = `/areas/${area.slug}`
    const seo = areaSeo[area.slug]
    const postcodes = splitList(area.postcodes)
    const description = seo?.description ?? area.intro ?? ''

    const place = {
        '@type': 'Place',
        name: area.name,
        ...(area.borough ? { containedInPlace: boroughNode(area.borough) } : {}),
        ...(postcodes.length ? {
            address: postcodes.map((postalCode) => ({
                '@type': 'PostalAddress',
                addressLocality: area.name,
                postalCode,
                ...(area.borough ? { addressRegion: area.borough } : {}),
                addressCountry: contact.country
            }))
        } : {})
    }

    return {
        '@context': 'https://schema.org',
        '@graph': [
            webPageNode({
                path,
                meta: {
                    title: seo?.title ?? pageTitle(`Builders in ${area.name}`),
                    description,
                    keywords: seo?.keywords
                },
                headline: `Builders in ${area.name}`,
                mainEntity: areaId(area.slug),
                about: areaId(area.slug)
            }),
            {
                '@type': 'Service',
                '@id': areaId(area.slug),
                name: `Builders in ${area.name}`,
                serviceType: 'Residential builders',
                description,
                ...(area.intro && area.intro !== description ? { disambiguatingDescription: area.intro } : {}),
                url: absoluteUrl(path),
                mainEntityOfPage: { '@id': `${absoluteUrl(path)}#webpage` },
                provider: { '@id': ORGANISATION_ID },
                areaServed: place,
                audience: { '@type': 'Audience', audienceType: `Homeowners in ${area.name}` },
                ...(seo?.keywords?.length ? { keywords: seo.keywords.join(', ') } : {}),
                offers: { ...QUOTATION_OFFER, areaServed: place },
                ...(services.length ? {
                    hasOfferCatalog: {
                        '@type': 'OfferCatalog',
                        name: `Building services in ${area.name}`,
                        itemListElement: services.map((service) => ({
                            '@type': 'Offer',
                            areaServed: { '@type': 'Place', name: area.name },
                            itemOffered: {
                                '@type': 'Service',
                                '@id': serviceId(service.slug),
                                name: service.title,
                                ...(serviceSeo[service.slug] ? {
                                    serviceType: serviceSeo[service.slug].keyword,
                                    alternateName: `${serviceSeo[service.slug].keyword} in ${area.name}`
                                } : {}),
                                url: absoluteUrl(`/services/${service.slug}`)
                            }
                        }))
                    }
                } : {})
            },
            websiteNode()
        ]
    }
}

export const faqSchema = (faqs?: Faq[] | null): Record<string, unknown> | null => {
    if (!faqs?.length) return null

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            }
        }))
    }
}
